import type {
  DashboardStats,
  DailyStats,
  CharacterAdmin,
  MediaAdmin,
  MediaReport,
  Report,
  BlocklistItem,
  BlocklistTestResult,
  AdminSettings,
  FeaturedList,
  PaginatedResponse,
  StudioBatchKickoffResponse,
  StudioDraftDetail,
  StudioDraftPatch,
  StudioRegenerateSectionKey,
  StudioDraftStatus,
  StudioDraftSubmit,
  StudioDraftSummary,
  StudioPublishResponse,
  StudioProvidersConfig,
  StudioProvidersUpdate,
  StudioTestChatProviderSummary,
  StudioTestChatProviderSummaryUpdate,
  StudioTestChatMessage,
  StudioTestChatPromptResponse,
  StudioTestChatTurnResponse,
} from '../types';

const API_BASE = '/api';

// Public asset base URLs for the community API
export const IMAGE_BASE_URL =
  'https://personallm-community-api.ai-proxy-project.workers.dev/images';
export const MEDIA_BASE_URL =
  'https://personallm-community-api.ai-proxy-project.workers.dev/media-assets';

async function fetchApi<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (response.status === 401) {
    // Session expired — redirect to login
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return response.json();
}

// Dashboard
export async function getDashboardStats(): Promise<DashboardStats> {
  return fetchApi('/stats/dashboard');
}

export async function getDailyStats(days: number = 30): Promise<DailyStats[]> {
  return fetchApi(`/stats/daily?days=${days}`);
}

// Characters
export async function getCharacters(params: {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'all' | 'flagged' | 'removed';
}): Promise<PaginatedResponse<CharacterAdmin>> {
  const query = new URLSearchParams();
  if (params.page) query.set('page', params.page.toString());
  if (params.limit) query.set('limit', params.limit.toString());
  if (params.search) query.set('search', params.search);
  if (params.status && params.status !== 'all') query.set('status', params.status);
  return fetchApi(`/characters?${query}`);
}

export async function removeCharacter(
  id: string,
  reason: string
): Promise<void> {
  // Soft-remove: set status to 'removed' with reason (not permanent delete)
  await fetchApi(`/characters/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ status: 'removed', reason }),
  });
}

export async function unflagCharacter(id: string): Promise<void> {
  await fetchApi(`/characters/${id}/unflag`, { method: 'POST' });
}

// Media
export async function getMedia(params: {
  page?: number;
  limit?: number;
  search?: string;
  type?: 'all' | 'image' | 'video';
  status?: 'all' | 'active' | 'flagged' | 'removed';
}): Promise<PaginatedResponse<MediaAdmin>> {
  const query = new URLSearchParams();
  if (params.page) query.set('page', params.page.toString());
  if (params.limit) query.set('limit', params.limit.toString());
  if (params.search) query.set('search', params.search);
  if (params.type && params.type !== 'all') query.set('type', params.type);
  if (params.status && params.status !== 'all') query.set('status', params.status);
  return fetchApi(`/media?${query}`);
}

export async function removeMedia(
  id: string,
  reason: string
): Promise<void> {
  await fetchApi(`/media/${id}/remove`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export async function unflagMedia(id: string): Promise<void> {
  await fetchApi(`/media/${id}/unflag`, { method: 'POST' });
}

// Media Reports
export async function getMediaReports(params: {
  page?: number;
  limit?: number;
  status?: 'pending' | 'reviewed' | 'all';
}): Promise<PaginatedResponse<MediaReport>> {
  const query = new URLSearchParams();
  query.set('content_type', 'media');
  if (params.page) query.set('page', params.page.toString());
  if (params.limit) query.set('limit', params.limit.toString());
  if (params.status && params.status !== 'all') query.set('status', params.status);
  return fetchApi(`/reports?${query}`);
}

export async function reviewMediaReport(
  id: string,
  action: 'dismiss' | 'remove_media' | 'flag_media'
): Promise<void> {
  await fetchApi(`/reports/${id}`, {
    method: 'POST',
    body: JSON.stringify({ action, contentType: 'media' }),
  });
}

// Reports
export async function getReports(params: {
  page?: number;
  limit?: number;
  status?: 'pending' | 'reviewed' | 'all';
}): Promise<PaginatedResponse<Report>> {
  const query = new URLSearchParams();
  if (params.page) query.set('page', params.page.toString());
  if (params.limit) query.set('limit', params.limit.toString());
  if (params.status && params.status !== 'all') query.set('status', params.status);
  return fetchApi(`/reports?${query}`);
}

export async function reviewReport(
  id: number,
  action: 'dismiss' | 'remove_character'
): Promise<void> {
  await fetchApi(`/reports/${id}`, {
    method: 'POST',
    body: JSON.stringify({ action }),
  });
}

// Bulk dismiss reports (uses allSettled so partial failures don't lose successes)
export async function bulkDismissReports(ids: number[]): Promise<void> {
  const results = await Promise.allSettled(
    ids.map((id) =>
      fetchApi(`/reports/${id}`, {
        method: 'POST',
        body: JSON.stringify({ action: 'dismiss' }),
      })
    )
  );
  const failed = results.filter((r) => r.status === 'rejected');
  if (failed.length > 0) {
    throw new Error(`${failed.length} of ${ids.length} dismissals failed`);
  }
}

export async function bulkDismissMediaReports(ids: string[]): Promise<void> {
  const results = await Promise.allSettled(
    ids.map((id) =>
      fetchApi(`/reports/${id}`, {
        method: 'POST',
        body: JSON.stringify({ action: 'dismiss', contentType: 'media' }),
      })
    )
  );
  const failed = results.filter((r) => r.status === 'rejected');
  if (failed.length > 0) {
    throw new Error(`${failed.length} of ${ids.length} dismissals failed`);
  }
}

// Studio
export async function getStudioDrafts(params: {
  page?: number;
  limit?: number;
  status?: StudioDraftStatus | 'all';
} = {}): Promise<PaginatedResponse<StudioDraftSummary>> {
  const query = new URLSearchParams();
  if (params.page) query.set('page', params.page.toString());
  if (params.limit) query.set('limit', params.limit.toString());
  if (params.status && params.status !== 'all') query.set('status', params.status);
  const suffix = query.toString() ? `?${query}` : '';
  return fetchApi(`/studio/drafts${suffix}`);
}

export async function getStudioDraft(id: string): Promise<StudioDraftDetail> {
  return fetchApi(`/studio/drafts/${id}`);
}

export async function submitStudioDraftBatch(
  drafts: StudioDraftSubmit[]
): Promise<StudioBatchKickoffResponse> {
  return fetchApi('/studio/drafts', {
    method: 'POST',
    body: JSON.stringify({ drafts }),
  });
}

export async function updateStudioDraft(
  id: string,
  patch: StudioDraftPatch
): Promise<StudioDraftDetail> {
  return fetchApi(`/studio/drafts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export function flushStudioDraftPatchKeepalive(
  id: string,
  patch: StudioDraftPatch
): void {
  if (Object.keys(patch).length === 0) {
    return;
  }
  void fetch(`${API_BASE}/studio/drafts/${id}`, {
    method: 'PATCH',
    credentials: 'include',
    keepalive: true,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(patch),
  }).catch(() => undefined);
}

export async function retryStudioDraft(id: string): Promise<unknown> {
  return fetchApi(`/studio/drafts/${id}/retry`, { method: 'POST' });
}

export async function regenerateStudioDraftSection(
  id: string,
  section: StudioRegenerateSectionKey
): Promise<StudioDraftDetail> {
  return fetchApi(`/studio/drafts/${id}/regenerate-section`, {
    method: 'POST',
    body: JSON.stringify({ section }),
  });
}

export async function publishStudioDraft(id: string): Promise<StudioPublishResponse> {
  return fetchApi(`/studio/drafts/${id}/publish`, { method: 'POST' });
}

export async function generateStudioDraftAvatar(
  id: string,
  body: {
    kind: 'still' | 'live';
    prompt?: string;
    motionPrompt?: string;
    preferredImageStyleRaw?: string;
  }
): Promise<StudioDraftDetail> {
  return fetchApi(`/studio/drafts/${id}/avatars`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function selectStudioDraftAvatar(
  id: string,
  avatarId: string
): Promise<StudioDraftDetail> {
  return fetchApi(`/studio/drafts/${id}/avatars/${avatarId}/select`, {
    method: 'POST',
  });
}

export async function getStudioProvidersConfig(): Promise<StudioProvidersConfig> {
  return fetchApi('/studio/providers');
}

export async function updateStudioProvidersConfig(
  update: StudioProvidersUpdate
): Promise<StudioProvidersConfig> {
  return fetchApi('/studio/providers', {
    method: 'PUT',
    body: JSON.stringify(update),
  });
}

export async function getStudioTestChatProviderSummary(): Promise<StudioTestChatProviderSummary> {
  return fetchApi('/studio/providers/testchat-summary');
}

export async function updateStudioTestChatProviderSummary(
  update: StudioTestChatProviderSummaryUpdate
): Promise<StudioTestChatProviderSummary> {
  return fetchApi('/studio/providers/testchat-summary', {
    method: 'PUT',
    body: JSON.stringify(update),
  });
}

export async function previewStudioTestChatPrompt(
  id: string,
  messages: StudioTestChatMessage[]
): Promise<StudioTestChatPromptResponse> {
  return fetchApi(`/studio/drafts/${id}/testchat/preview`, {
    method: 'POST',
    body: JSON.stringify({ messages }),
  });
}

export async function sendStudioTestChatTurn(
  id: string,
  messages: StudioTestChatMessage[]
): Promise<StudioTestChatTurnResponse> {
  return fetchApi(`/studio/drafts/${id}/testchat`, {
    method: 'POST',
    body: JSON.stringify({ messages }),
  });
}

// Settings
export async function getSettings(): Promise<AdminSettings> {
  return fetchApi('/settings');
}

export async function updateSettings(settings: Partial<AdminSettings>): Promise<AdminSettings> {
  return fetchApi('/settings', {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
}

// Featured cover
export async function getFeatured(): Promise<FeaturedList> {
  return fetchApi('/featured');
}

export async function updateFeatured(ids: string[]): Promise<FeaturedList> {
  return fetchApi('/featured', {
    method: 'PUT',
    body: JSON.stringify({ ids }),
  });
}

// Blocklist
export async function getBlocklist(): Promise<{ entries: BlocklistItem[] }> {
  return fetchApi('/blocklist');
}

export async function addBlocklistItem(
  value: string,
  type: 'word' | 'phrase' | 'pattern' = 'word'
): Promise<{ entry: BlocklistItem }> {
  return fetchApi('/blocklist', {
    method: 'POST',
    body: JSON.stringify({ type, value }),
  });
}

export async function removeBlocklistItem(id: string): Promise<void> {
  await fetchApi(`/blocklist/${id}`, { method: 'DELETE' });
}

export async function testBlocklist(text: string): Promise<BlocklistTestResult> {
  return fetchApi('/blocklist/test', {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}
